import { ContextMenuSelect, DataGrid } from '@turnpike/portal-data-grid';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDeleteTaskMutation, useTasksInfiniteQuery } from '../api';
import { filterByStatus } from '../model/utils/status';

import { useTableLayoutColumns } from './TableLayout.columns';
import Styles from './TableLayout.module.scss';

import type { TaskModel } from '../model/types/task.model';
import type { FilterStatusType } from '@/app/types/TaskStatusFilter';

import { useAppSelector } from '@/app/hooks/useAppSelector';
import { EditIcon, DeleteIcon, TasksIcon } from '@/components/Elements/Icons';
import { ConfirmDeleteMessage } from '@/components/Modals/ConfirmDeleteMessage';
import { ConfirmDeleteModal } from '@/components/Modals/ConfirmDeleteModal';
import { useOrganizationsDictionary } from '@/features/Shared/hooks/useOrganizations';

export const TableLayout = () => {
	const [deleteItem, setDeleteItem] = useState<TaskModel | null>(null);
	const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
	const [searchParams] = useSearchParams();
	const search = searchParams.get('filter') ?? '';
	const status = (searchParams.get('status') as FilterStatusType) ?? 'all';
	const filter = useAppSelector((state) => state.filter);

	const { currentData, fetchNextPage, isLoading, isFetching } = useTasksInfiniteQuery({
		...filter,
		title: search,
	});

	const organizations = useOrganizationsDictionary();
	const items = useMemo(
		() => currentData?.pages?.flatMap((page) => page.items) ?? [],
		[currentData]
	);
	const isLoadingData = isLoading || (!currentData && isFetching);
	const filteredItems = useMemo(() => filterByStatus(items, status), [items, status]);

	const { columns } = useTableLayoutColumns({
		filter: status,
		organizations,
	});

	return (
		<div className={Styles.container}>
			<div className={Styles.gridContainer}>
				<DataGrid
					isLoading={isLoadingData}
					onEndReached={fetchNextPage}
					data={filteredItems}
					columns={columns}
					renderActions={(row) => {
						return (
							<ContextMenuSelect>
								<ContextMenuSelect.Option
									title="Edit"
									icon={<EditIcon />}
									onClick={() => console.log('edit')}
								/>
								<ContextMenuSelect.Option
									title="Delete"
									icon={<DeleteIcon />}
									onClick={() => setDeleteItem(row)}
								/>
							</ContextMenuSelect>
						);
					}}
				/>
				{deleteItem && (
					<ConfirmDeleteModal
						title="Do you want to delete this task?"
						isLoading={isDeleting}
						onCloseRequest={() => setDeleteItem(null)}
						onConfirm={async () => {
							await deleteTask(deleteItem);
							setDeleteItem(null);
						}}
					>
						<ConfirmDeleteMessage
							iconBackground="#0cc7d7"
							icon={<TasksIcon />}
							message={deleteItem.title}
						/>
					</ConfirmDeleteModal>
				)}
			</div>
		</div>
	);
};
